<template>
  <div class="instructor-admin-page">
    <div class="d-flex">
      <nav id="breadcrumbs" class="mb-3">
        <ul>
          <li>
            <router-link :to="{ name: 'company.dashboard' }">
              <i class="uil-home-alt"></i>
            </router-link>
          </li>
          <li>{{ $t("general.instructors") }}</li>
          <li>{{ $t("general.instructor_list") }}</li>
        </ul>
      </nav>
    </div>

    <div class="section-header mb-lg-2 border-0 uk-flex-middle">
      <div class="section-header-left">
        <h2 class="uk-heading-line text-left">
          <span>{{ $t("general.instructor_list") }}</span>
        </h2>
      </div>
      <div class="section-header-right">
        <button
          class="btn btn-default"
          type="button"
          :disabled="!$hasPermission('create_instructor')"
          @click="goToCreatePage"
        >
          <i class="uil-plus"></i> {{ $t("general.add_instructor") }}
        </button>
      </div>
    </div>

    <div
      v-if="notificationMessage"
      :class="
        notificationType === 'error' ? 'uk-alert-danger' : 'uk-alert-success'
      "
      uk-alert
    >
      <a class="uk-alert-close" uk-close></a>
      <p
        class="mb-0"
        :class="notificationType === 'error' ? 'text-danger' : 'text-success'"
      >
        <i
          :class="
            notificationType === 'error'
              ? 'uil-exclamation-triangle'
              : 'uil-check-circle'
          "
        ></i>
        {{ notificationMessage }}
      </p>
    </div>

    <div class="card">
      <div class="card-header actions-toolbar border-0 instructor-toolbar-card">
        <div class="d-flex justify-content-between instructor-toolbar">
          <div class="uk-search uk-search-navbar instructor-search">
            <i class="uil-search position-absolute"></i>
            <input
              v-model.trim="filters.search"
              class="uk-search-input shadow-0 uk-form-small"
              type="search"
              :placeholder="$t('general.search')"
            />
          </div>

          <div class="d-flex instructor-toolbar-actions">
            <div class="btn-group mr-3">
              <select v-model.number="perPage" class="ml-1" @change="applyFilters">
                <option :value="10">10</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
                <option :value="500">500</option>
                <option :value="1000">1000</option>
                <option :value="2500">2500</option>
              </select>
            </div>

            <a
              :uk-tooltip="$t('filters.order.sort')"
              class="btn btn-icon btn-hover btn-circle"
              href="javascript:void(0)"
            >
              <i class="uil-list-ui-alt"></i>
            </a>
            <div
              class="uk-dropdown"
              uk-dropdown="pos: bottom-right; mode: click; animation: uk-animation-scale-up"
            >
              <ul class="uk-nav uk-dropdown-nav">
                <li>
                  <a href="javascript:void(0)" @click="sortInstructors('asc', 'name')">
                    {{ $t("sorting.a_z") }}
                  </a>
                </li>
                <li>
                  <a href="javascript:void(0)" @click="sortInstructors('desc', 'name')">
                    {{ $t("sorting.z_a") }}
                  </a>
                </li>
                <li>
                  <a href="javascript:void(0)" @click="sortInstructors('asc', 'id')">
                    {{ $t("sorting.created_at_old") }}
                  </a>
                </li>
                <li>
                  <a href="javascript:void(0)" @click="sortInstructors('desc', 'id')">
                    {{ $t("sorting.created_at_new") }}
                  </a>
                </li>
              </ul>
            </div>

            <a
              :class="{ 'instructor-filter-button--active': hasActiveListFilters }"
              :uk-tooltip="$t('general.filter_list')"
              class="btn btn-icon btn-hover btn-circle"
              href="javascript:void(0)"
            >
              <i class="uil-filter"></i>
            </a>
            <div
              ref="instructorFilterDropdown"
              class="uk-dropdown large instructor-filter-dropdown"
              uk-dropdown="pos: bottom-right; mode: click; animation: uk-animation-scale-up"
            >
              <h4 class="mb-3 instructor-filter-title">
                <i class="uil-filter"></i> {{ $t("general.filter_list") }}
              </h4>

              <div class="instructor-filter-container">
                <div class="uk-child-width-1-2@s uk-grid-small" uk-grid>
                  <div>
                    <h5 class="uk-text-bold mb-2">
                      {{ $t("general.instructor_type") }}
                    </h5>
                    <select
                      v-model="filterDraft.type"
                      class="form-control instructor-filter-select"
                    >
                      <option value="">
                        {{ $t("general.all_instructor_types") }}
                      </option>
                      <option :value="instructorTypes.INTERNAL">
                        {{ $t("general.internal_instructor") }}
                      </option>
                      <option :value="instructorTypes.EXTERNAL">
                        {{ $t("general.external_instructor") }}
                      </option>
                    </select>
                  </div>

                  <div>
                    <h5 class="uk-text-bold mb-2">
                      {{ $t("users.title") }}
                    </h5>
                    <multiselect
                      v-model="filterDraft.titles"
                      :allow-empty="true"
                      :clear-on-select="false"
                      :close-on-select="false"
                      :deselect-label="$t('general.deselect')"
                      :loading="titlesLoading"
                      :multiple="true"
                      :options="titleOptions"
                      :placeholder="$t('general.select')"
                      :preserve-search="true"
                      :searchable="true"
                      :select-label="$t('general.select')"
                      :selected-label="$t('general.selected')"
                      label="name"
                      track-by="id"
                    />
                  </div>

                  <div>
                    <h5 class="uk-text-bold mb-2">
                      {{ $t("general.instructor_status") }}
                    </h5>
                    <div class="instructor-status-options">
                      <label>
                        <input
                          v-model="filterDraft.status"
                          class="uk-radio"
                          type="radio"
                          value=""
                        />
                        {{ $t("general.all") }}
                      </label>
                      <label>
                        <input
                          v-model="filterDraft.status"
                          :value="instructorStatuses.ACTIVE"
                          class="uk-radio"
                          type="radio"
                        />
                        {{ $t("general.active") }}
                      </label>
                      <label>
                        <input
                          v-model="filterDraft.status"
                          :value="instructorStatuses.PASSIVE"
                          class="uk-radio"
                          type="radio"
                        />
                        {{ $t("general.passive") }}
                      </label>
                    </div>
                  </div>
                </div>

                <div class="uk-flex uk-flex-right mt-3 instructor-filter-actions">
                  <button class="btn btn-light mr-2" type="button" @click="clearListFilters">
                    {{ $t("general.clear_all") }}
                  </button>
                  <button class="btn btn-primary grey" type="button" @click="applyFilters">
                    <i class="uil-filter"></i> {{ $t("general.filter") }}
                  </button>
                </div>
              </div>
            </div>

            <excel-download-button
              :loading="isReportDownloading"
              :tooltipText="$t('filters.export_excell')"
              @download="exportInstructors"
            />
          </div>
        </div>
      </div>

      <default-loading v-if="loading"></default-loading>

      <div v-else-if="loadError" class="instructor-list-state">
        <i class="uil-exclamation-circle text-danger"></i>
        <span>{{ loadError }}</span>
        <button
          class="btn btn-light btn-sm"
          type="button"
          @click="loadInstructors"
        >
          {{ $t("general.try_again") }}
        </button>
      </div>

      <template v-else>
        <div class="d-flex justify-content-between mr-3 ml-3 instructor-bulk-summary">
          <div>
            <span class="mr-2">
              {{ $t("general.active_instructors") }}: {{ counts.active }}
            </span>
            <span class="mr-2">
              {{ $t("general.passive_instructors") }}: {{ counts.passive }}
            </span>
            <span class="mr-2">
              {{ $t("general.total_instructors") }}: {{ counts.total }}
            </span>
          </div>
          <span>
            {{ $t("general.selected_instructors") }}:
            <i class="uil-graduation-hat"></i>
            {{ selectedInstructorIds.length }}
          </span>
        </div>

        <div class="p-3 d-flex disabled_hover users-bulk-actions instructor-bulk-actions">
          <button
            class="btn btn-sm btn-secondary mr-2 mr-auto p-2"
            type="button"
            :disabled="!instructors.length || bulkProcessing"
            @click="toggleAllVisibleInstructors"
          >
            <i class="uil-check-square"></i>
            {{
              $t(
                allVisibleSelected
                  ? "general.deselect_all"
                  : "general.select_all"
              )
            }}
          </button>
          <button
            class="btn btn-sm btn-success"
            type="button"
            :disabled="
              !hasSelection ||
              bulkProcessing ||
              !$hasPermission('update_instructor')
            "
            @click="bulkUpdateInstructorStatus(instructorStatuses.ACTIVE)"
          >
            <i class="uil-check"></i>
            {{ $t("general.activate_selected_instructors") }}
          </button>
          <button
            class="btn btn-sm btn-danger ml-2"
            type="button"
            :disabled="
              !hasSelection ||
              bulkProcessing ||
              !$hasPermission('update_instructor')
            "
            @click="bulkUpdateInstructorStatus(instructorStatuses.PASSIVE)"
          >
            <i class="uil-minus-circle"></i>
            {{ $t("general.deactivate_selected_instructors") }}
          </button>
          <button
            class="btn btn-sm btn-secondary ml-2"
            type="button"
            :disabled="
              !hasSelection ||
              bulkProcessing ||
              !$hasPermission('delete_instructor')
            "
            @click="bulkDeleteInstructors"
          >
            <i class="uil-trash"></i>
            {{ $t("general.delete_selected_instructors") }}
          </button>
        </div>

        <div class="table-responsive">
          <table class="table align-items-center instructor-table">
            <thead>
              <tr>
                <th scope="col"></th>
                <th scope="col">{{ $t("general.instructor") }}</th>
              <th scope="col">{{ $t("general.title_position") }}</th>
              <th scope="col">{{ $t("general.expertise_areas") }}</th>
              <th scope="col">{{ $t("general.instructor_type") }}</th>
              <th scope="col">{{ $t("general.status") }}</th>
              <th scope="col">{{ $t("general.created_at") }}</th>
              <th scope="col">
                <span class="sr-only">{{ $t("general.actions") }}</span>
              </th>
              </tr>
            </thead>
            <tbody class="list">
              <tr v-for="instructor in instructors" :key="instructor.id">
                <th>
                  <input
                    :checked="selectedInstructorIds.includes(instructor.id)"
                    :id="`instructor-checkbox-${instructor.id}`"
                    :value="instructor.id"
                    class="instructor-checkbox"
                    type="checkbox"
                    @change="
                      toggleInstructorSelection(
                        instructor.id,
                        $event.target.checked
                      )
                    "
                  />
                </th>
                <th scope="row">
                <div class="media align-items-center instructor-identity">
                  <img
                    :src="instructor.photo"
                    :alt="instructor.name"
                    class="instructor-avatar"
                    @error="useFallbackPhoto"
                  />
                  <div class="media-body ml-3">
                    <button
                      class="instructor-name-button"
                      type="button"
                      :disabled="!$hasPermission('show_instructor')"
                      @click="openDetailModal(instructor)"
                    >
                      {{ instructor.name }}
                    </button>
                    <small class="d-block text-muted">
                      {{
                        instructor.institutionName ||
                        instructor.department ||
                        "—"
                      }}
                    </small>
                  </div>
                </div>
              </th>
              <td>
                <span class="instructor-title">
                  {{ instructor.title || "—" }}
                </span>
              </td>
              <td>
                <div
                  v-if="
                    instructor.expertiseAreas &&
                    instructor.expertiseAreas.length
                  "
                  class="instructor-tags"
                >
                  <span
                    v-for="expertise in instructor.expertiseAreas.slice(0, 2)"
                    :key="expertise"
                    class="instructor-tag"
                  >
                    {{ expertise }}
                  </span>
                  <span
                    v-if="instructor.expertiseAreas.length > 2"
                    class="instructor-tag instructor-tag--more"
                    :uk-tooltip="getExpertiseTooltip(instructor.expertiseAreas)"
                  >
                    +{{ instructor.expertiseAreas.length - 2 }}
                  </span>
                </div>
                <span v-else class="text-muted">—</span>
              </td>
              <td>
                <span
                  class="instructor-type"
                  :class="`instructor-type--${instructor.type}`"
                >
                  {{ getTypeLabel(instructor.type) }}
                </span>
              </td>
              <td>
                <span
                  class="instructor-status"
                  :class="`instructor-status--${getStatusClass(
                    instructor.statusId
                  )}`"
                >
                  <span class="instructor-status__dot"></span>
                  {{ getStatusLabel(instructor.statusId) }}
                </span>
              </td>
              <td>
                <span class="instructor-date">
                  {{ formatDate(instructor.createdAt) }}
                </span>
              </td>
              <td class="text-right">
                <div class="actions instructor-actions">
                  <button
                    v-if="$hasPermission('show_instructor')"
                    class="btn btn-icon btn-hover btn-sm btn-circle"
                    type="button"
                    :uk-tooltip="$t('general.view_detail')"
                    @click="openDetailModal(instructor)"
                  >
                    <i class="uil-eye"></i>
                  </button>
                  <button
                    v-if="$hasPermission('update_instructor')"
                    class="btn btn-icon btn-hover btn-sm btn-circle"
                    type="button"
                    :uk-tooltip="$t('general.edit')"
                    @click="goToEditPage(instructor)"
                  >
                    <i class="uil-edit"></i>
                  </button>
                  <button
                    v-if="$hasPermission('update_instructor')"
                    class="btn btn-icon btn-hover btn-sm btn-circle"
                    type="button"
                    :uk-tooltip="
                      instructor.statusId === instructorStatuses.ACTIVE
                        ? $t('general.do_passive')
                        : $t('general.do_active')
                    "
                    @click="toggleInstructorStatus(instructor)"
                  >
                    <i
                      :class="
                        instructor.statusId === instructorStatuses.ACTIVE
                          ? 'uil-pause-circle text-warning'
                          : 'uil-play-circle text-success'
                      "
                    ></i>
                  </button>
                  <button
                    v-if="$hasPermission('delete_instructor')"
                    class="btn btn-icon btn-hover btn-sm btn-circle"
                    type="button"
                    :uk-tooltip="$t('general.delete')"
                    @click="deleteInstructor(instructor)"
                  >
                    <i class="uil-trash-alt text-danger"></i>
                  </button>
                </div>
              </td>
              </tr>
            </tbody>
          </table>
        </div>
        <pagination
          v-if="pagination"
          :pagination="pagination"
          class="mt-2 mb-3"
          @changePage="setPage($event)"
        ></pagination>
      </template>
    </div>

    <div
      v-if="detailTarget"
      class="instructor-admin-modal"
      @click.self="closeDetailModal"
    >
      <div
        class="instructor-admin-modal__dialog instructor-admin-modal__dialog--detail"
        role="dialog"
        aria-modal="true"
        aria-labelledby="instructor-detail-title"
      >
        <div class="instructor-admin-modal__header">
          <div>
            <span class="instructor-admin-modal__eyebrow">{{
              $t("general.instructor_detail")
            }}</span>
            <h3 id="instructor-detail-title">{{ detailTarget.name }}</h3>
          </div>
          <button
            type="button"
            class="instructor-admin-modal__close"
            :aria-label="$t('general.close')"
            @click="closeDetailModal"
          >
            &times;
          </button>
        </div>

        <div class="instructor-detail-profile">
          <img
            :src="detailTarget.photo"
            :alt="detailTarget.name"
            @error="useFallbackPhoto"
          />
          <div>
            <strong>{{
              detailTarget.title || $t("general.title_not_specified")
            }}</strong>
            <span>
              {{ getTypeLabel(detailTarget.type) }} ·
              {{ getStatusLabel(detailTarget.statusId) }}
            </span>
            <span>
              {{
                detailTarget.institutionName ||
                detailTarget.department ||
                $t("general.institution_not_specified")
              }}
            </span>
          </div>
        </div>

        <div class="instructor-detail-section">
          <h4>{{ $t("general.expertise_areas") }}</h4>
          <div
            v-if="
              detailTarget.expertiseAreas && detailTarget.expertiseAreas.length
            "
            class="instructor-tags"
          >
            <span
              v-for="expertise in detailTarget.expertiseAreas"
              :key="expertise"
              class="instructor-tag"
            >
              {{ expertise }}
            </span>
          </div>
          <span v-else class="text-muted">{{
            $t("general.expertise_area_not_specified")
          }}</span>
        </div>

        <div class="instructor-detail-section">
          <h4>{{ $t("general.educations_and_certificates") }}</h4>
          <div
            v-if="
              detailTarget.educationsCertificates &&
              detailTarget.educationsCertificates.length
            "
            class="instructor-certificates"
          >
            <span
              v-for="(certificate, index) in detailTarget.educationsCertificates"
              :key="`instructor-certificate-${index}`"
              class="instructor-certificate"
            >
              <i class="uil-award"></i>
              {{ certificate }}
            </span>
          </div>
          <span v-else class="text-muted">—</span>
        </div>

        <div class="instructor-detail-section">
          <h4>{{ $t("general.about") }}</h4>
          <p>{{
            detailTarget.bio || $t("general.instructor_description_not_found")
          }}</p>
        </div>

        <div class="instructor-detail-grid">
          <div>
            <span>{{ $t("general.experience") }}</span>
            <strong>
              {{
                detailTarget.experienceYears ? detailTarget.experienceYears : "—"
              }}
            </strong>
          </div>
          <div>
            <span>{{ $t("general.institution") }}</span>
            <strong>{{ detailTarget.institutionName || "—" }}</strong>
          </div>
          <div>
            <span>{{ $t("general.created_at") }}</span>
            <strong>{{ formatDate(detailTarget.createdAt) }}</strong>
          </div>
        </div>

        <div class="instructor-admin-modal__footer">
          <button class="btn btn-light" type="button" @click="closeDetailModal">
            {{ $t("general.close") }}
          </button>
          <button
            v-if="$hasPermission('update_instructor')"
            class="btn btn-default"
            type="button"
            @click="editDetailTarget"
          >
            <i class="uil-edit"></i> {{ $t("general.edit") }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import instructorService, {
  INSTRUCTOR_STATUSES,
  INSTRUCTOR_TYPES,
} from "@/core/services/instructor.service";
import Pagination from "@/assets/components/Pagination.vue";
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import ExcelDownloadButton from "@/view/components/buttons/ExcelDownloadButton.vue";
import { confirmDialogDelete } from "@/utils/confirmDialog";
import Multiselect from "vue-multiselect";

const FALLBACK_PHOTO = "https://cdn.anzera.com.tr/softinya/not-found.png";

export default {
  name: "InstructorList",
  components: { DefaultLoading, ExcelDownloadButton, Multiselect, Pagination },
  data() {
    return {
      instructors: [],
      selectedInstructorIds: [],
      pagination: null,
      page: 1,
      perPage: 10,
      sortField: "id",
      sortDirection: "desc",
      loading: false,
      bulkProcessing: false,
      isReportDownloading: false,
      titlesLoading: false,
      loadError: "",
      titleOptions: [],
      counts: {
        active: 0,
        passive: 0,
        total: 0,
      },
      filters: {
        search: "",
        type: "",
        status: "",
        titles: [],
      },
      filterDraft: {
        type: "",
        status: "",
        titles: [],
      },
      detailTarget: null,
      notificationMessage: "",
      notificationType: "success",
      notificationTimer: null,
      filterTimer: null,
      instructorTypes: INSTRUCTOR_TYPES,
      instructorStatuses: INSTRUCTOR_STATUSES,
    };
  },
  computed: {
    hasActiveListFilters() {
      return Boolean(
        this.filters.type ||
        this.filters.status !== "" ||
        this.filters.titles.length
      );
    },
    hasSelection() {
      return this.selectedInstructorIds.length > 0;
    },
    allVisibleSelected() {
      return (
        this.instructors.length > 0 &&
        this.instructors.every((instructor) =>
          this.selectedInstructorIds.includes(instructor.id)
        )
      );
    },
  },
  watch: {
    "filters.search"() {
      this.page = 1;
      window.clearTimeout(this.filterTimer);
      this.filterTimer = window.setTimeout(() => {
        this.loadInstructors();
      }, 500);
    },
  },
  methods: {
    toggleInstructorSelection(id, checked) {
      if (checked) {
        if (!this.selectedInstructorIds.includes(id)) {
          this.selectedInstructorIds.push(id);
        }
        return;
      }

      this.selectedInstructorIds = this.selectedInstructorIds.filter(
        (instructorId) => instructorId !== id
      );
    },
    toggleAllVisibleInstructors() {
      const visibleIds = this.instructors.map((instructor) => instructor.id);

      if (this.allVisibleSelected) {
        this.selectedInstructorIds = this.selectedInstructorIds.filter(
          (id) => !visibleIds.includes(id)
        );
        return;
      }

      this.selectedInstructorIds = Array.from(
        new Set([...this.selectedInstructorIds, ...visibleIds])
      );
    },
    async bulkUpdateInstructorStatus(statusId) {
      if (!this.hasSelection || this.bulkProcessing) return;

      this.bulkProcessing = true;
      const selectedIds = [...this.selectedInstructorIds];
      try {
        await instructorService.bulkUpdateStatus(selectedIds, statusId);
        this.selectedInstructorIds = [];
        await Promise.all([this.loadInstructors(), this.loadCounts()]);
      } catch (error) {
        this.showError(
          this.getErrorMessage(
            error,
            this.$t("general.instructor_status_update_failed")
          )
        );
      } finally {
        this.bulkProcessing = false;
      }
    },
    async bulkDeleteInstructors() {
      if (!this.hasSelection || this.bulkProcessing) return;

      const isConfirmed = await confirmDialogDelete({ context: this });
      if (!isConfirmed) return;

      this.bulkProcessing = true;
      const selectedIds = [...this.selectedInstructorIds];
      try {
        await instructorService.bulkRemove(selectedIds);
        this.selectedInstructorIds = [];
        await Promise.all([this.loadInstructors(), this.loadCounts()]);
        if (!this.instructors.length && this.page > 1) {
          this.page -= 1;
          await this.loadInstructors();
        }
      } catch (error) {
        this.showError(
          this.getErrorMessage(
            error,
            this.$t("general.instructor_delete_failed")
          )
        );
      } finally {
        this.bulkProcessing = false;
      }
    },
    async loadInstructors() {
      this.loading = true;
      this.loadError = "";

      try {
        const result = await instructorService.list({
          like: this.filters.search || undefined,
          type: this.filters.type || undefined,
          status_id:
            this.filters.status === "" ? undefined : this.filters.status,
          title_id: this.getSelectedTitleIdsParam(),
          page: this.page,
          per_page: this.perPage,
          sort: this.sortField,
          order: this.sortDirection,
        });
        this.instructors = result.items;
        this.pagination = result.pagination;
      } catch (error) {
        this.loadError = this.getErrorMessage(
          error,
          this.$t("general.instructor_list_load_failed")
        );
      } finally {
        this.loading = false;
      }
    },
    showRouteSuccess() {
      if (this.$route.query.action === "created") {
        this.showSuccess(this.$t("general.instructor_created_successfully"));
      } else if (this.$route.query.action === "updated") {
        this.showSuccess(this.$t("general.instructor_updated_successfully"));
      }
    },
    async loadCounts() {
      try {
        this.counts = await instructorService.counts();
      } catch (error) {
        this.counts = { active: 0, passive: 0, total: 0 };
      }
    },
    async loadTitleOptions() {
      this.titlesLoading = true;
      try {
        this.titleOptions = await instructorService.listTitles();
      } catch (error) {
        this.titleOptions = [];
      } finally {
        this.titlesLoading = false;
      }
    },
    getSelectedTitleIdsParam() {
      if (!this.filters.titles.length) return undefined;
      return JSON.stringify(this.filters.titles.map((title) => title.id));
    },
    clearListFilters() {
      this.filterDraft.type = "";
      this.filterDraft.status = "";
      this.filterDraft.titles = [];
      this.filters.type = "";
      this.filters.status = "";
      this.filters.titles = [];
      this.page = 1;
      this.closeFilterDropdown();
      this.loadInstructors();
    },
    applyFilters() {
      this.filters.type = this.filterDraft.type;
      this.filters.status = this.filterDraft.status;
      this.filters.titles = [...this.filterDraft.titles];
      this.page = 1;
      this.closeFilterDropdown();
      this.loadInstructors();
    },
    closeFilterDropdown() {
      const dropdownElement = this.$refs.instructorFilterDropdown;
      const uiKit = window.UIkit;
      if (!dropdownElement || !uiKit) return;

      uiKit.dropdown(dropdownElement).hide(0);
    },
    setPage(page) {
      this.page = page;
      this.loadInstructors();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    sortInstructors(order, field) {
      this.sortDirection = order;
      this.sortField = field;
      this.page = 1;
      this.loadInstructors();
    },
    async exportInstructors() {
      if (this.isReportDownloading) return;

      this.isReportDownloading = true;
      try {
        const response = await instructorService.export({
          like: this.filters.search || null,
          type: this.filters.type || null,
          status_id: this.filters.status === "" ? null : this.filters.status,
          title_id: this.getSelectedTitleIdsParam() || null,
          sort: this.sortField,
          order: this.sortDirection,
        });
        const fileUrl = window.URL.createObjectURL(new Blob([response.data]));
        const fileLink = document.createElement("a");
        const date = new Date();
        const timestamp = [
          date.getFullYear(),
          String(date.getMonth() + 1).padStart(2, "0"),
          String(date.getDate()).padStart(2, "0"),
          String(date.getHours()).padStart(2, "0"),
          String(date.getMinutes()).padStart(2, "0"),
          String(date.getSeconds()).padStart(2, "0"),
        ].join("");

        fileLink.href = fileUrl;
        fileLink.setAttribute("download", `instructors_list_${timestamp}.xlsx`);
        document.body.appendChild(fileLink);
        fileLink.click();
        fileLink.remove();
        window.URL.revokeObjectURL(fileUrl);
      } catch (error) {
        this.showError(
          this.getErrorMessage(
            error,
            this.$t("general.instructor_export_failed")
          )
        );
      } finally {
        this.isReportDownloading = false;
      }
    },
    getTypeLabel(type) {
      return type === INSTRUCTOR_TYPES.EXTERNAL
        ? this.$t("general.external_instructor")
        : this.$t("general.internal_instructor");
    },
    getStatusLabel(status) {
      return Number(status) === INSTRUCTOR_STATUSES.PASSIVE
        ? this.$t("general.passive")
        : this.$t("general.active");
    },
    getStatusClass(status) {
      return Number(status) === INSTRUCTOR_STATUSES.PASSIVE
        ? "passive"
        : "active";
    },
    getExpertiseTooltip(expertiseAreas) {
      return `title: ${expertiseAreas.join(", ")}; pos: top`;
    },
    formatDate(date) {
      if (!date) return "—";
      return new Intl.DateTimeFormat("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(new Date(date));
    },
    useFallbackPhoto(event) {
      if (event.target.src !== FALLBACK_PHOTO)
        event.target.src = FALLBACK_PHOTO;
    },
    goToCreatePage() {
      this.$router.push({ name: "company.instructors.add" });
    },
    goToEditPage(instructor) {
      this.$router.push({
        name: "company.instructors.edit",
        params: { id: instructor.id },
      });
    },
    async openDetailModal(instructor) {
      this.detailTarget = instructor;
      this.lockBody();
      try {
        this.detailTarget = await instructorService.get(instructor.id);
      } catch (error) {
        this.showError(
          this.getErrorMessage(
            error,
            this.$t("general.instructor_detail_load_failed")
          )
        );
      }
    },
    closeDetailModal() {
      this.detailTarget = null;
      this.unlockBodyIfAvailable();
    },
    editDetailTarget() {
      const instructor = this.detailTarget;
      this.closeDetailModal();
      this.goToEditPage(instructor);
    },
    async toggleInstructorStatus(instructor) {
      const nextStatus =
        instructor.statusId === INSTRUCTOR_STATUSES.ACTIVE
          ? INSTRUCTOR_STATUSES.PASSIVE
          : INSTRUCTOR_STATUSES.ACTIVE;
      try {
        await instructorService.updateStatus(instructor.id, nextStatus);
        await Promise.all([this.loadInstructors(), this.loadCounts()]);
        this.showSuccess(
          nextStatus === INSTRUCTOR_STATUSES.ACTIVE
            ? this.$t("general.instructor_activated_successfully")
            : this.$t("general.instructor_deactivated_successfully")
        );
      } catch (error) {
        this.showError(
          this.getErrorMessage(
            error,
            this.$t("general.instructor_status_update_failed")
          )
        );
      }
    },
    async deleteInstructor(instructor) {
      const isConfirmed = await confirmDialogDelete({ context: this });
      if (!isConfirmed) return;

      try {
        await instructorService.remove(instructor.id);
        this.selectedInstructorIds = this.selectedInstructorIds.filter(
          (id) => id !== instructor.id
        );
        if (this.instructors.length === 1 && this.page > 1) this.page -= 1;
        await Promise.all([this.loadInstructors(), this.loadCounts()]);
      } catch (error) {
        this.showError(
          this.getErrorMessage(
            error,
            this.$t("general.instructor_delete_failed")
          )
        );
      }
    },
    showSuccess(message) {
      this.showNotification(message, "success");
    },
    showError(message) {
      this.showNotification(message, "error");
    },
    showNotification(message, type) {
      this.notificationMessage = message;
      this.notificationType = type;
      window.clearTimeout(this.notificationTimer);
      this.notificationTimer = window.setTimeout(() => {
        this.notificationMessage = "";
      }, 3000);
    },
    lockBody() {
      document.body.style.overflow = "hidden";
    },
    unlockBodyIfAvailable() {
      if (!this.detailTarget) {
        document.body.style.overflow = "";
      }
    },
    handleKeydown(event) {
      if (event.key !== "Escape") return;
      if (this.detailTarget) {
        this.closeDetailModal();
      }
    },
    getErrorMessage(error, fallback) {
      const responseData = error?.response?.data;
      if (responseData?.message) return responseData.message;
      if (responseData?.error) return responseData.error;
      if (responseData?.errors) {
        return Object.values(responseData.errors).flat().join(" ");
      }
      return error?.message || fallback;
    },
  },
  mounted() {
    this.loadInstructors();
    this.loadCounts();
    this.loadTitleOptions();
    this.showRouteSuccess();
    document.addEventListener("keydown", this.handleKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleKeydown);
    document.body.style.overflow = "";
    window.clearTimeout(this.notificationTimer);
    window.clearTimeout(this.filterTimer);
  },
};
</script>

<style scoped>
@import "~vue-multiselect/dist/vue-multiselect.min.css";

.instructor-page-description {
  margin: -5px 0 0;
  color: #7b8796;
  font-size: 13px;
}

.instructor-toolbar {
  display: flex;
  align-items: center;
  width: 100%;
}

.instructor-toolbar-actions {
  align-items: center;
  flex-shrink: 0;
}

.instructor-toolbar-actions select {
  min-height: 34px;
}

.instructor-search {
  width: auto;
  flex: 1;
  margin-right: 12px;
}

.instructor-search i {
  top: 9px;
  left: 10px;
  z-index: 1;
}

.instructor-search input {
  padding-left: 34px;
}

.instructor-filter-dropdown.large {
  width: min(770px, calc(100vw - 30px)) !important;
  padding: 24px;
  overflow: visible !important;
}

.instructor-filter-title {
  color: #8a8d96;
  font-weight: 700;
}

.instructor-filter-title i {
  color: #73c92d;
}

.instructor-filter-container {
  display: flex;
  min-height: 185px;
  flex-direction: column;
  justify-content: space-between;
  overflow: visible !important;
}

.instructor-filter-container h5 {
  color: #8a8d96;
}

.instructor-filter-select {
  min-height: 43px;
  border-color: #dfe3e8;
  color: #667085;
}

.instructor-filter-actions {
  padding-top: 8px;
}

.instructor-status-options {
  display: flex;
  min-height: 43px;
  align-items: center;
  gap: 14px;
}

.instructor-status-options label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: #344054;
  font-weight: 600;
}

.instructor-filter-button--active {
  color: #2f6fe4;
  background: #edf3ff;
}

.instructor-bulk-summary {
  align-items: center;
  min-height: 34px;
  font-size: 14px;
}

.instructor-bulk-actions {
  flex-wrap: wrap;
  gap: 8px;
}

.instructor-bulk-actions .btn {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.instructor-bulk-actions .btn:first-child {
  margin-right: auto !important;
}

.instructor-checkbox {
  width: 15px;
  height: 15px;
  margin: 10px 10px 10px 0;
}

.instructor-list-state {
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  color: #718096;
}

.instructor-list-state i {
  font-size: 30px;
}

.instructor-table th,
.instructor-table td {
  vertical-align: middle;
}

.instructor-identity {
  min-width: 205px;
}

.instructor-avatar {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  background: #eef2f6;
  object-fit: cover;
  object-position: center top;
}

.instructor-name-button {
  padding: 0;
  border: 0;
  background: transparent;
  color: #344054;
  font-size: 13px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}

.instructor-name-button:hover {
  color: #2f6fe4;
}

.instructor-name-button:disabled {
  color: #344054;
  cursor: default;
}

.instructor-title {
  display: inline-block;
  min-width: 150px;
  color: #475467;
  font-size: 12px;
}

.instructor-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  min-width: 170px;
}

.instructor-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 7px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #526174;
  font-size: 10px;
  font-weight: 600;
}

.instructor-tag--more {
  background: #e8effd;
  color: #356ac3;
}

.instructor-type,
.instructor-status {
  display: inline-flex;
  align-items: center;
  padding: 5px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
}

.instructor-type--internal {
  background: #edf3ff;
  color: #356ac3;
}

.instructor-type--external {
  background: #f4edff;
  color: #7a4ab5;
}

.instructor-status {
  gap: 5px;
}

.instructor-status--active {
  background: #e8f7ef;
  color: #25845a;
}

.instructor-status--passive {
  background: #f2f4f7;
  color: #667085;
}

.instructor-status__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.instructor-date {
  color: #667085;
  font-size: 11px;
  white-space: nowrap;
}

.instructor-actions {
  display: inline-flex;
  gap: 2px;
  min-width: 144px;
  justify-content: flex-end;
}

.instructor-admin-modal {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.62);
  backdrop-filter: blur(3px);
}

.instructor-admin-modal__dialog {
  width: min(590px, 100%);
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  padding: 24px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.3);
}

.instructor-admin-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.instructor-admin-modal__header h3 {
  margin: 2px 0 0;
  font-size: 21px;
}

.instructor-admin-modal__eyebrow {
  color: #7c8ba1;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-transform: uppercase;
}

.instructor-admin-modal__close {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  padding: 0;
  border: 1px solid #dce4ec;
  border-radius: 50%;
  background: #f8fafc;
  color: #5f6f82;
  font-size: 23px;
  cursor: pointer;
}

.instructor-detail-profile {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border: 1px solid #e4eaf1;
  border-radius: 12px;
  background: #f8fafc;
}

.instructor-detail-profile img {
  width: 78px;
  height: 78px;
  border-radius: 50%;
  background: #eef2f6;
  object-fit: cover;
  object-position: center top;
}

.instructor-detail-profile div {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.instructor-detail-profile strong {
  color: #344054;
}

.instructor-detail-profile span {
  color: #718096;
  font-size: 12px;
}

.instructor-detail-section {
  margin-top: 20px;
}

.instructor-detail-section h4 {
  margin-bottom: 8px;
  color: #344054;
  font-size: 13px;      
}

.instructor-detail-section p {
  margin: 0;
  color: #667085;
  font-size: 12px;
  line-height: 1.7;
  word-wrap: break-word;
}

.instructor-certificates {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.instructor-certificate {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #667085;
  font-size: 12px;
}

.instructor-certificate i {
  color: #356ac3;
  font-size: 15px;
}

.instructor-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 20px;
}

.instructor-detail-grid > div {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 12px;
  border-radius: 9px;
  background: #f8fafc;
}

.instructor-detail-grid span {
  color: #8491a3;
  font-size: 10px;
}

.instructor-detail-grid strong {
  overflow-wrap: anywhere;
  color: #475467;
  font-size: 12px;
}

.instructor-admin-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #e7edf3;
}

@media (max-width: 900px) {
  .instructor-toolbar {
    flex-wrap: wrap;
    gap: 10px;
  }

  .instructor-search {
    width: 100%;
  }

  .instructor-toolbar-actions {
    width: 100%;
    justify-content: flex-end;
  }

}

@media (max-width: 640px) {
  .instructor-toolbar-actions {
    flex-wrap: wrap;
  }

  .instructor-bulk-summary {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }

  .instructor-bulk-actions .btn {
    width: 100%;
  }

  .instructor-bulk-actions .btn:first-child {
    margin-right: 0 !important;
  }

  .instructor-admin-modal {
    padding: 10px;
  }

  .instructor-admin-modal__dialog {
    max-height: calc(100vh - 20px);
    padding: 18px;
  }

  .instructor-detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
